import './AttachFileTile.scss';
import { CSSProperties, FC, KeyboardEvent, useState } from 'react';
import clsx from 'clsx';
import { ActionIcon } from '@mantine/core';
import { MdOutlineDelete } from 'react-icons/md';
import { HiOutlinePencil } from 'react-icons/hi2';
import { IOption } from '@srm/ui/types/IOption';
import Input from '@srm/ui/input';
import Select from '@srm/ui/select';
import Skeleton from '@srm/ui/skeleton';
import { formatBytes } from '@srm/ui/lib/formatBytes';
import { fileType } from '@srm/ui/lib/fileTypeInfo';
import { useFileThumbnail } from './useFileThumbnail';

/** Редактируемое свойство файла в плитке; с `options` — выбор из справочника. */
export interface IAttachFileField {
    key: string;
    label: string;
    options?: IOption[];
}

export interface IAttachFileTileProps {
    row: any;
    layout?: 'row' | 'card';
    /** Бинарник нового файла; у уже сохранённого вложения его нет — превью не строим. */
    file?: File;
    fields: IAttachFileField[];
    /** Имя правится через свойство AttachmentFileName (расширение держит onCommit). */
    nameEditable: boolean;
    onCommit: (row: any, field: string, value: unknown) => void;
    onRemove: (row: any) => void;
}

const NAME_FIELD = 'AttachmentFileName';

const extOf = (name: string): string => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1) : '';
};

const AttachFileTile: FC<IAttachFileTileProps> = ({
    row,
    file,
    fields,
    nameEditable,
    onCommit,
    onRemove,
    layout = 'card',
}) => {
    const name: string = row[NAME_FIELD] ?? row._filename ?? '';
    const ext = extOf(row._filename ?? name);
    const type = fileType(ext);
    const Icon = type.Icon;
    const thumb = useFileThumbnail(layout === 'card' ? file : undefined);
    const [editingName, setEditingName] = useState(false);

    const commitName = (value: string) => {
        setEditingName(false);
        if (value !== name) onCommit(row, NAME_FIELD, value);
    };

    const onNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            // Enter в модалке — submit формы
            e.preventDefault();
            commitName(e.currentTarget.value);
        } else if (e.key === 'Escape') {
            // Escape закрыл бы модалку целиком
            e.stopPropagation();
            setEditingName(false);
        }
    };

    return (
        <article
            className={clsx('aft', layout === 'row' && 'aft--row')}
            data-testid={layout === 'card' ? 'attach-file-tile' : 'attach-file-row'}
            style={
                {
                    '--aft-file-accent': type.accent,
                    '--aft-file-accent-soft': type.accentSoft,
                } as CSSProperties
            }
        >
            <div className="aft__summary">
                <div className={clsx('aft__preview', thumb && `aft__preview--${thumb.kind}`)}>
                    {thumb === undefined ? (
                        <Skeleton className="aft__preview-skeleton" />
                    ) : thumb.kind === 'image' ? (
                        <img className="aft__image" src={thumb.src} alt="" draggable={false} />
                    ) : thumb.kind === 'page' ? (
                        <img className="aft__page" src={thumb.src} alt="" draggable={false} />
                    ) : thumb.kind === 'text' ? (
                        <pre className="aft__text" aria-hidden>
                            {thumb.text}
                        </pre>
                    ) : (
                        <span className="aft__mark">
                            <Icon aria-hidden />
                        </span>
                    )}
                </div>

                <div className="aft__body">
                    {editingName ? (
                        <input
                            className="aft__name-input"
                            defaultValue={name}
                            aria-label="Имя файла"
                            autoFocus
                            onFocus={(e) => {
                                // выделяем основу имени без расширения — как в проводнике
                                const dot = e.currentTarget.value.lastIndexOf('.');
                                e.currentTarget.setSelectionRange(0, dot > 0 ? dot : e.currentTarget.value.length);
                            }}
                            onBlur={(e) => commitName(e.currentTarget.value)}
                            onKeyDown={onNameKeyDown}
                        />
                    ) : (
                        <span
                            className="aft__name"
                            title={name}
                            onDoubleClick={() => nameEditable && setEditingName(true)}
                        >
                            {name}
                        </span>
                    )}

                    <div className="aft__meta">{row._fileSize != null ? formatBytes(row._fileSize) : type.label}</div>
                </div>
            </div>
            {fields.length > 0 && (
                <div className="aft__fields">
                    {fields.map((field) => {
                        const value = row[field.key];
                        const id = `aft-${row._fileId}-${field.key}`;
                        return (
                            <div
                                key={field.key}
                                className="aft__field"
                                role={field.options ? 'group' : undefined}
                                aria-labelledby={field.options ? `${id}-label` : undefined}
                            >
                                {field.options ? (
                                    <span id={`${id}-label`} className="aft__field-label">
                                        {field.label}
                                    </span>
                                ) : (
                                    <label className="aft__field-label" htmlFor={id}>
                                        {field.label}
                                    </label>
                                )}
                                {field.options ? (
                                    <Select
                                        options={field.options}
                                        size="sm"
                                        onlySelect
                                        // поле в плитке узкое — список по ширине значений
                                        dropdownMaxWidth={360}
                                        values={
                                            value != null && value !== ''
                                                ? [{ value: String(value), label: String(value) }]
                                                : undefined
                                        }
                                        onChange={(option: IOption[]) => {
                                            if (option.length > 0) onCommit(row, field.key, option[0].value);
                                        }}
                                    />
                                ) : (
                                    <Input
                                        id={id}
                                        size="xs"
                                        defaultValue={value == null ? '' : String(value)}
                                        onBlur={(e) => {
                                            if (e.currentTarget.value !== (value ?? ''))
                                                onCommit(row, field.key, e.currentTarget.value);
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="aft__actions">
                {nameEditable && (
                    <ActionIcon
                        type="button"
                        variant="subtle"
                        color="gray"
                        size={32}
                        className="aft__rename"
                        title="Переименовать"
                        aria-label={`Переименовать: ${name}`}
                        disabled={editingName}
                        onClick={() => setEditingName(true)}
                    >
                        <HiOutlinePencil size={18} aria-hidden />
                    </ActionIcon>
                )}
                <ActionIcon
                    type="button"
                    variant="subtle"
                    color="gray"
                    size={32}
                    className="aft__remove"
                    aria-label={`Убрать файл: ${name}`}
                    title="Убрать файл"
                    onClick={() => onRemove(row)}
                >
                    <MdOutlineDelete size={18} aria-hidden />
                </ActionIcon>
            </div>
        </article>
    );
};

export default AttachFileTile;
